/**
 * Manifest sync hook — mount once in App.tsx.
 *
 * On mount:  prefetches the manifest and stores it in refDataStore.
 * On focus:  re-fetches the manifest, diffs per-catalog ETags against the
 *            stored values, and invalidates only the catalogs that changed.
 *
 * This ensures that reference-data updates are picked up without blasting
 * the entire ['ref'] query key (which would re-fetch all catalogs at once).
 *
 * IMPORTANT: catalogEtags is read from the live store (getState()) inside each
 * sync call — never captured as a closure snapshot — so that focus events
 * always diff against the current store state, not the state at mount time.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { fetchManifest } from '@/api/refData';
import { useRefDataStore } from './refDataStore';
import type { RefManifest } from '@/schemas/refData';

async function syncManifest(
  getCatalogEtags: () => Record<string, string>,
  setManifest: (m: RefManifest) => void,
  invalidate: (name: string) => Promise<void>,
): Promise<void> {
  try {
    const manifest = await fetchManifest();

    // Read live store state BEFORE calling setManifest so the diff compares
    // the pre-update snapshot against the freshly fetched manifest.
    // Using getState() here (not a mount-time closure) ensures each sync
    // call sees the store as it was at the moment the manifest arrived —
    // not the stale value from when the effect was first created.
    const catalogEtags = getCatalogEtags();

    setManifest(manifest);

    for (const catalog of manifest.catalogs) {
      const known = catalogEtags[catalog.name];
      if (known !== catalog.etag) {
        await invalidate(catalog.name);
      }
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[useManifestSync] Manifest sync failed:', err);
    }
    // Manifest sync is best-effort — never crash the app.
  }
}

export function useManifestSync(): void {
  const queryClient = useQueryClient();
  const setManifest = useRefDataStore((s) => s.setManifest);

  useEffect(() => {
    // Always read from live store, not a captured snapshot.
    function getCatalogEtags(): Record<string, string> {
      return useRefDataStore.getState().catalogEtags;
    }

    async function invalidate(name: string): Promise<void> {
      await queryClient.invalidateQueries({ queryKey: ['ref', name] });
    }

    // Prefetch on mount — reads live store at call time.
    void syncManifest(getCatalogEtags, setManifest, invalidate);

    function handleFocus(): void {
      // Reads live store at focus time, not at mount time.
      void syncManifest(getCatalogEtags, setManifest, invalidate);
    }

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally run only once on mount
}
